import {Checkbox, CircularProgress, MenuItem, Pagination, Select, Stack, Typography} from '@mui/material';
import {DataGrid, GridCellParams} from '@mui/x-data-grid';
import React, {FunctionComponent, useEffect, useState} from 'react';
import {Info} from 'react-feather';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';
import {CheckboxInputChecked, CheckboxInputEmpty, errorBigIcon, plug, toastSuccess} from '../../../assets/icons/icons';
import {EPlatformsNames, ERepoBoardStatus, ESubscriptionPlans, ETrialStatus} from '../../../models/consts';
import {IItem, IPlatformUser, SubPlanInfo} from '../../../models/models';
import Platform from '../../../models/platforms-data';
import {CustomTooltip} from '../../../styles/components/CustomTooltip';
import {Button, Chip} from '../../StyledComponents';
import {IItemRow} from './models';
import './style.scss';
import {IDailyStatusBody, IError} from "../../../models/inner-models";
import Swal from "sweetalert2";
import {decoratePopUpMessage} from "../../../utils/popUpTextDecorator";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {setStatusDailyBackup} from "../../../store/actions";
import {useWebSocket} from "../../../contexts/WebSocketContext";
import {updatePlatformState} from "../../../store/reducers/platform";
import {updateSubscriptionPlanModal} from "../../../utils/functions/updateSubscriptionPlanModal";
import {EWebsocketMethods} from "../../../constants";
import {isSubUser} from "../../../store/selectors";
import {CalendarTick, PauseCircle, PlayCircle, Refresh} from "iconsax-reactjs";
import SortAsc from '../../../assets/icons/filterAsc.svg?react';
import SortDesc from '../../../assets/icons/filterDesc.svg?react';

interface IBackupItems {
  id: string;
  name: string;
  isRepo?: boolean;
  projectTypeKey?: string;
  subPlatform?: string;
}

interface IPlatformListProps {
  items: Array<IItem>;
  platformName: EPlatformsNames;
  subPlatform?: string;
  onDelete?: (id: string, name: string, type?: string) => void;
  onBulkDelete?: (arr: string[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // onBackup: (id: string, name: string, type?: boolean) => Record<string, any>;
  subPlan: SubPlanInfo;
  setSelectAll: (boolean) => void;
  selectAll: boolean;
  setSelectList: (string) => void;
  updItems: () => void;
  selectList: Array<string>;
  trialState?: string;
  appWasDeleted?: boolean;
  users?: IPlatformUser[];
  total: number;
  page: number;
  setPage: (number) => void;
  openTutorial?: () => void;
  downloadZipLatest?: () => void;
  isLoading: boolean;
  isAdmin: boolean;
}

const PlatformItemsList: FunctionComponent<IPlatformListProps> = ({
                                                                    items,
                                                                    updItems,
                                                                    platformName,
                                                                    subPlatform,
                                                                    // onDelete,
                                                                    onBulkDelete,
                                                                    // onBackup,
                                                                    subPlan,
                                                                    selectAll,
                                                                    selectList,
                                                                    setSelectAll,
                                                                    setSelectList,
                                                                    trialState,
                                                                    appWasDeleted,
                                                                    users,
                                                                    page,
                                                                    setPage,
                                                                    total,
                                                                    isLoading,
                                                                    openTutorial,
                                                                    isAdmin,
                                                                    downloadZipLatest
                                                                  }: IPlatformListProps) => {
    const {t: translate} = useTranslation();
    const [rows, setRows] = useState<Array<IItemRow>>([]);
    const [cacheRows, setCacheRows] = useState<Array<IItemRow>>([]);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [updating, setIsUpdating] = useState<boolean>(true);
    const [updatingDaily, setIsUpdatingDaily] = useState<boolean>(false);
    const pageSize = 10;
    const hasSelectedListActiveItem = selectList?.some(item => rows.find(rowItem => (rowItem.itemId === item))?.isActive)
    const hasSelectedListNoProcessItem = selectList?.some(item => !rows.find(rowItem => (rowItem.itemId === item))?.actionProcess)
    const hasSelectedActiveListNoProcessItem = selectList?.some(item => {
      const currentRow = rows.find(rowItem => (rowItem.itemId === item))
      return !currentRow?.actionProcess && currentRow?.isActive
    })
    const {onMessage} = useWebSocket();
    const isUserSub = useAppSelector(isSubUser);

    useEffect(() => {
      setIsUpdating(true);
      setRows(items.map(({
                           id,
                           size,
                           lastBackup,
                           isDailyActive,
                           actionProcess,
                           projectTypeKey,
                           board,
                           repo,
                           project,
                           workspace,
                           name,
                           isActive,
                           status,
                           isRepo,
                           description,
                           typeManage
                         }, idx) => ({
        name: repo || project || board || workspace || name || '-',
        platformName: platformName,
        isActive: isActive,
        isDailyActive: isDailyActive,
        actionProcess: actionProcess,
        id: idx,
        itemId: id,
        lastBackup: lastBackup,
        projectTypeKey: projectTypeKey,
        size: size,
        status: status,
        isRepo: !!isRepo || !subPlatform,
        description: description || '',
        typeManage: typeManage || ''
      } as IItemRow)));
      setIsUpdating(false);
    }, [platformName, items, subPlan]);

    useEffect(() => {
      setCacheRows(prevState => {
        rows.forEach(item => {
          if (prevState.findIndex(i => i.itemId === item.itemId) === -1) {
            prevState.push(item)
          }
        })
        return [...prevState]
      })
    }, [rows])

    useEffect(() => {

      onMessage((data) => {
        if (![EWebsocketMethods.itemsAddedCountChange, EWebsocketMethods.updateDashboardPlatformItemsAmount].includes(data.method)) {
          dispatch(updatePlatformState(data))
        }
      });

    }, []);

    const redirectToBackup = (run: boolean, id: string, name: string, type?: boolean, projectTypeKey?: string, status?: string) => {
      navigate(`/platform/backups/${platformName.toLowerCase()}?name=${encodeURIComponent(name?.split('::')[0])}&id=${id}${(platformName === 'github' || platformName === 'gitlab') && !type ? '&type=sub' : ''}${!!users?.length ? ('&user=' + users[0].username) : ''}${projectTypeKey ? '&projectTypeKey=' + projectTypeKey : ''}${[EPlatformsNames.TRELLO, EPlatformsNames.GITHUB].includes(platformName) && status ? '&status=' + status : ''}${run ? '&run=true' : ''}${subPlatform ? `&subType=${subPlatform}` : ''}`);
    };

    const setDailyStatus = async (active: boolean, single?) => {
      try {
        const payload: IDailyStatusBody = {is_daily_backed_up: active}
        setIsUpdatingDaily(true)
        selectList = selectList?.filter(item => {
          const row = rows.find(rowItem => (rowItem.itemId === item))
          return row?.isActive && !row?.actionProcess && row?.status !== ERepoBoardStatus.INITIALIZED
        })
        switch (platformName) {
          case 'trello':
            payload.boardsIds = single ? [single.itemId] : selectList
            break;
          case 'github':
            if (subPlatform) {
              payload.gistsIds = single ? [single.itemId] : selectList
            } else {
              payload.reposIds = single ? [single.itemId] : selectList
            }
            break;
          case 'gitlab':
            if (subPlatform) {
              payload.snippetsIds = single ? [single.itemId] : selectList
            } else {
              payload.projectsIds = single ? [single.itemId] : selectList
            }
            break;
          case 'jira':
            payload.projectsIds = single ? [single.itemId] : selectList
            break;
          case 'notion':
            payload.workspacesIds = single ? [single.itemId] : selectList
            break;
          default:
            break;
        }
        await dispatch(setStatusDailyBackup({payload: payload, platform: platformName})).unwrap();

        if (!single) {
          setIsUpdating(true);
        }
        setRows(prevState => {
          const tempArr = prevState.map(prevItem => {
            if ((single && prevItem.itemId === single.itemId) || (!single && selectList.indexOf(prevItem.itemId) !== -1)) {
              return {...prevItem, isDailyActive: active}
            } else {
              return prevItem
            }
          })
          return [...tempArr]
        })
        setSelectAll(false)
        updItems()
        setIsUpdating(false);
        setIsUpdatingDaily(false);

        await Swal.fire({
          title: translate('notifications.titles.success'),
          text: single ?
            active ? translate('notifications.all.activeStatus') : translate('notifications.all.inactiveStatus') :
            active ? translate('notifications.all.activeStatus_many') : translate('notifications.all.inactiveStatus_many'),
          toast: true,
          position: 'top-end',
          timerProgressBar: true,
          showConfirmButton: false,
          showCloseButton: true,
          imageUrl: toastSuccess,
          timer: 3000,
        });
      } catch (err) {
        const error = err as IError;

        await Swal.fire({
          title: translate('notifications.titles.error'),
          text: decoratePopUpMessage(error.error as string),
          imageUrl: errorBigIcon,
          confirmButtonText: translate('notifications.choices.close'),
        });
      }
    }

    const columns = [
      {
        field: 'checkbox',
        minWidth: 40,
        maxWidth: 40,
        editable: false,
        sortable: false,
        headerClassName: 'items-table--header',
        cellClassName: (params) =>
          selectList.indexOf(params.row.itemId) !== -1 || selectAll ? 'items-table-cell--check checked' : 'items-table-cell--check',
        renderHeader: () => {
          return (
            rows.length === 1 ?
              <div/> :
              <Checkbox
                color="primary"
                checked={selectAll}
                disabled={!rows?.length || !isAdmin}
                onChange={(event) => {
                  setSelectAll(event.target.checked as boolean)
                }}
                icon={<CheckboxInputEmpty/>}
                checkedIcon={<CheckboxInputChecked/>}
              />
          );
        },
        renderCell: (params: GridCellParams) => {
          return (
            <>
              {rows.length !== 1 && (
                <Checkbox
                  color="primary"
                  checked={selectList.indexOf(params.row.itemId) !== -1 || selectAll}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setSelectList(params.row.itemId)
                  }}
                  disabled={!isAdmin || params.row.lastBackup === '-' && params.row.actionProcess || params.row.status === ERepoBoardStatus.INITIALIZED}
                  icon={<CheckboxInputEmpty/>}
                  checkedIcon={<CheckboxInputChecked/>}
                />
              )}
            </>
          );
        }
      },
      {
        field: 'name',
        headerName: platformName === 'shopify' ? subPlatform : subPlatform ? (platformName === 'github' ? 'gists' : 'snippets') : translate(`views.bulk_restore.itemName.${platformName}.many`),
        flex: 1,
        editable: false,
        sortable: true,
        cellClassName: 'items-table-cell--name',
        headerClassName: 'items-table--header',
        renderCell: (params: GridCellParams) => {
          const data = params.value.split('::')
          return (
            <>
              {params.row.status === ERepoBoardStatus.DELETED &&
                <div className="name-block info-img">
                  <CustomTooltip
                    title={`This was deleted in  ${Platform[platformName]?.title}. Download ${platformName === 'trello' ? 'or Single Restore' : platformName !== 'notion' ? 'or Restore' : 'backup'} to rescue the ${subPlatform ? (platformName === 'github' ? 'gist' : 'snippet') : translate(`views.bulk_restore.itemName.${platformName}.one`)}.`}>
                    <Info className='delete-tooltip'/>
                  </CustomTooltip>
                </div>
              }
              <CustomTooltip title={
                params.row.description ?
                  `${data[0]} - ${params.row.description}` :
                  params.row.typeManage ?
                    `${data[0]} - ${params.row.typeManage}` :
                    data[0]
              }>
                <div className="name-block">
                  <div>
                    <span style={{fontWeight: 500}}>{data[0]}</span>
                    {params.row.description}
                    {data[1] && <span className='item-private-type'>{data[1]}</span>}
                  </div>
                </div>
              </CustomTooltip>
            </>
          );
        },
      },
      {
        field: 'daily',
        headerName: 'Backup Schedule',
        minWidth: 170,
        editable: false,
        sortable: false,
        headerClassName: 'items-table--header',
        renderCell: (params: GridCellParams) => {
          return (
            <div
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setDailyStatus(!params.row.isDailyActive, params.row)
              }}
              className='daily-status-cell-btn'
              aria-disabled={!isAdmin || !params.row.isActive || updatingDaily || appWasDeleted || params.row.status === ERepoBoardStatus.DELETED || params.row.actionProcess || params.row.status === ERepoBoardStatus.INITIALIZED}
            >
              {!params.row.isDailyActive ?
                <PlayCircle size={20} style={{marginRight: '5px'}} color='#3B82F6'/> :
                <PauseCircle size={20} style={{marginRight: '5px'}} color='#BBC2D1'/>
              }
              Daily Backups: <span>{params.row.isDailyActive ? 'ON' : 'OFF'}</span>
            </div>
          );
        }
      },
      {
        field: 'lastBackup',
        headerName: 'Last backup',
        minWidth: 165,
        maxWidth: 200,
        flex: 0.08,
        editable: false,
        sortable: false,
        headerClassName: 'items-table--header',
        renderCell: (params: GridCellParams) => {
          return (
            <div className="date-block">
              {params.value === '-' ? 'No backup' : params.value}
            </div>
          );
        },
      },
      {
        field: 'size',
        headerName: 'Size',
        minWidth: 90,
        maxWidth: 160,
        flex: 0.08,
        editable: false,
        sortable: false,
        headerClassName: 'items-table--header',
        renderCell: (params: GridCellParams) => {
          return (
            <div className="size-block">
              {params.value || '-'}
            </div>
          );
        },
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 144,
        editable: false,
        sortable: false,
        headerClassName: 'items-table--header',
        renderCell: (params: GridCellParams) => (
          params.value
            ?
            <Chip
              className={!!params.row.actionProcess?.length || params.row.status === 'initialized' ? 'process active-process' : 'success'}
              label={
                !!params.row.actionProcess?.length ?
                  translate(`notifications.all.process.${params.row.actionProcess}`) : params.row.status === 'initialized' && !params.row.actionProcess?.length ? translate('subscriptions.statuses.initialized') :
                    translate('subscriptions.statuses.protected')
              }
            />
            : (
              <CustomTooltip
                title={`${params.row.status === ERepoBoardStatus.DELETED ? translate('views.platform_pages.backups_disabled_del', {platform: Platform[platformName]?.title}) : translate('views.platform_pages.backups_disabled')} ${params.row.status === ERepoBoardStatus.DELETED && platformName === 'trello' ? 'You can only make a single restore.' : ''}`}>
                <Chip
                  className={'error'}
                  label={translate('subscriptions.statuses.inactive')}
                />
              </CustomTooltip>
            )
        ),
      },
      {
        field: 'id',
        headerName: 'Actions',
        width: 270,
        editable: false,
        sortable: false,
        headerClassName: 'items-table--header',
        valueGetter: () => '',
        renderCell: (params: GridCellParams) => (
          <div
            className="action-buttons platforms-btn table-actions"
            style={{visibility: !!selectList.length ? 'hidden' : 'visible'}}
          >
            <Button
              variant="contained" color="secondary"
              disabled={params.row.lastBackup === '-' && (!subPlan?.isPaid || (subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED))}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                redirectToBackup(false, params.row.itemId, params.row.name, params.row.isRepo, params.row.projectTypeKey, params.row.status)
              }}
            >
              {translate('common.buttons.view')}
            </Button>
            {platformName !== 'notion' && platformName !== 'chatgpt' && platformName !== 'claude' && (
              <Button variant="outlined" color="primary"
                      disabled={(!subPlan?.isPaid) || !isAdmin || platformName === EPlatformsNames.NOTION || params.row.lastBackup === '-' || updatingDaily || appWasDeleted || params.row.actionProcess || params.row.status === ERepoBoardStatus.INITIALIZED ||
                        (isUserSub && !subPlan.isPaid && trialState !== ETrialStatus.STARTED) || (subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED)
                      }
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        redirectToBackup(true, params.row.itemId, params.row.name, params.row.isRepo, params.row.projectTypeKey, params.row.status)
                      }}
              >
                <Refresh size={16} style={{marginRight: 5}}/>
                {translate('common.buttons.restore')}
              </Button>
            )}
            {(platformName === 'chatgpt' || platformName === 'claude') && (
              <Button variant="outlined" color="primary"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (downloadZipLatest) downloadZipLatest(params.row.itemId)
                      }}>
                Get PDF/MD
              </Button>
            )}
            {onBulkDelete && platformName !== 'shopify' && (
              <CustomTooltip title='Deactivate'>
                <Button variant="text" color="error"
                        disabled={!isAdmin}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          onBulkDelete([params.row.itemId])
                        }}>
                  <img src={plug} alt="plug" style={{width: 20, height: 20}}/>
                  {/*{translate('common.buttons.delete')}*/}
                </Button>
              </CustomTooltip>
            )}
          </div>
        ),
      },
    ];

    const PaginationFooter = () => {
      const itemFrom = (pageSize * (page + 1)) - pageSize + 1
      const itemTo = (pageSize * (page + 1)) <= total ? (pageSize * (page + 1)) : total
      return (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          height='64px'
          px='16px'
        >
          <Typography>{total === 0 ? 'Showing 0 of 0' : `Showing ${itemFrom} to ${itemTo} of ${total}`}</Typography>
          <Pagination
            count={Math.ceil(total / pageSize)}
            page={page + 1}
            onChange={(e, v) => {
              setPage(v - 1)
            }}
          />
        </Stack>

      )
    };

    return (
      <>
        {updating ?
          <div className="spinner-wrapper">
            <CircularProgress/>
          </div> :
          <>
            <div className='header-block custom-table-control'>
              {platformName !== 'notion' && platformName !== 'chatgpt' && platformName !== 'claude' && (
                <CustomTooltip
                  title={selectList.length > 10 ? 'You can restore up to 10 items at a time' : ((subPlan?.name !== 'Pro' && subPlan?.name !== 'Trial') && selectList.length !== 1) ? 'This feature is only available with a Pro Plan subscription' : ''}>
                  <Button variant="text" color="primary"
                          disabled={(subPlan && !subPlan.isPaid) || (subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) ||
                            (subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED) ||
                            appWasDeleted || !!selectList?.filter(f => !!rows.find(r => (r.itemId === f && r.lastBackup === '-')))?.length ||
                            !hasSelectedListNoProcessItem || selectList.length > 10}
                          onClick={() => {
                            console.log(subPlan?.name)
                            if ((subPlan?.name !== 'Pro' && subPlan?.name !== 'Trial') && selectList.length !== 1) {
                              return updateSubscriptionPlanModal({translate, platformName, navigate})
                            }

                            if (selectList.length === 1) {
                              const singleItem = cacheRows.find(({itemId}) => itemId === selectList[0])
                              if (singleItem) {
                                redirectToBackup(true, singleItem.itemId, singleItem.name, singleItem.isRepo, singleItem.projectTypeKey, singleItem.status)
                              }
                              return
                            }
                            localStorage['bulkPlatform'] = platformName
                            const tempArr: IBackupItems[] = []
                            selectList.forEach(item => {
                              const result = cacheRows.find(({itemId}) => itemId === item)

                              if (result?.actionProcess || result?.status === 'deleted') {
                                return
                              }

                              tempArr.push({
                                id: item,
                                name: result?.name || '-',
                                isRepo: result?.isRepo || false,
                                projectTypeKey: result?.projectTypeKey,
                                subPlatform: subPlatform || ''
                              })
                            })
                            if (platformName === 'jira') {
                              Swal.fire({
                                title: 'Jira: Restore Confirmation',
                                html: '<div><p><div>1. Create a new project and copy the settings of the original</div><div>2. When you restore, choose a designated project to restore to</div></p> <p><b>Warning!</b> If you do not, it will duplicate your items on the same project</p></div>',
                                showCancelButton: true,
                                showDenyButton: true,
                                denyButtonText: 'Tutorial',
                                cancelButtonText: translate('notifications.choices.cancel'),
                                confirmButtonText: translate('notifications.choices.continue'),
                                imageUrl: errorBigIcon,
                                showLoaderOnConfirm: true,
                              })
                                .then(res => {
                                  if (res.isConfirmed) {
                                    localStorage['selectedItem'] = JSON.stringify(tempArr)
                                    setTimeout(() => navigate('/bulk-restore'), 50)
                                  } else if (res.isDenied) {
                                    openTutorial && openTutorial()
                                  }
                                })
                            } else {
                              localStorage['selectedItem'] = JSON.stringify(tempArr)
                              setTimeout(() => navigate('/bulk-restore'), 50)
                            }
                          }}
                  >
                    <Refresh size={16} style={{marginRight: 5}}/>
                    {translate('common.buttons.restore')}
                  </Button>
                </CustomTooltip>
              )}
              {platformName !== 'shopify' && platformName !== 'chatgpt' && platformName !== 'claude' && (
                <Select
                  value='more'
                  className='daily-select'
                  MenuProps={{
                    id: 'daily-backup-select',
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                  disabled={(!subPlan?.isPaid) || (subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) ||
                    (subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED) || appWasDeleted || !hasSelectedListActiveItem || !hasSelectedActiveListNoProcessItem}
                >
                  <MenuItem value='more' disabled>
                    <CalendarTick size={16} style={{marginRight: 5}}/>
                    Daily Backup
                  </MenuItem>
                  <div className='daily-backup-select-count'>For {selectList?.length} selected</div>

                  {platformName !== 'shopify' && platformName !== 'chatgpt' && platformName !== 'claude' && (
                    <CustomTooltip
                      title={(subPlan?.name !== 'Pro' && subPlan?.name !== 'Trial') ? 'This feature is only available with a Pro Plan subscription' : ''}>
                      <Button variant="text" color="primary"
                              onClick={() => (
                                (subPlan?.name !== 'Pro' && subPlan?.name !== 'Trial') ?
                                  updateSubscriptionPlanModal({
                                    translate,
                                    platformName,
                                    navigate
                                  }) :
                                  setDailyStatus(true)
                              )}
                              disabled={(!subPlan?.isPaid) || (subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) ||
                                (subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED) || appWasDeleted || !hasSelectedListActiveItem || !hasSelectedActiveListNoProcessItem}
                      >
                        <span style={{color: "#22C55E", marginRight: '5px'}}>&#x2022;</span> Enable Daily Backup
                      </Button>
                    </CustomTooltip>
                  )}
                  {platformName !== 'shopify' && platformName !== 'chatgpt' && platformName !== 'claude' && (
                    <CustomTooltip
                      title={(subPlan?.name !== 'Pro' && subPlan?.name !== 'Trial') ? 'This feature is only available with a Pro Plan subscription' : ''}>
                      <Button variant="text" color="primary"
                              onClick={() => (
                                (subPlan?.name !== 'Pro' && subPlan?.name !== 'Trial') ?
                                  updateSubscriptionPlanModal({
                                    translate,
                                    platformName,
                                    navigate
                                  }) :
                                  setDailyStatus(false)
                              )}
                              disabled={(!subPlan?.isPaid) || (subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) ||
                                (subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED) || appWasDeleted || !hasSelectedListActiveItem || !hasSelectedActiveListNoProcessItem}
                      >
                        <span style={{color: "#EF4444", marginRight: '5px'}}>&#x2022;</span> Disable Daily Backup
                      </Button>
                    </CustomTooltip>
                  )}
                </Select>
              )}
              {platformName !== 'shopify' && (
                <Button variant="text" color="error"
                        disabled={!selectList.length || !hasSelectedListNoProcessItem}
                        onClick={() => {
                          selectList = selectList?.filter(item => {
                            const row = rows.find(rowItem => (rowItem.itemId === item))
                            return !row?.actionProcess
                          })
                          onBulkDelete && onBulkDelete(selectList)
                        }}
                >
                  <img src={plug} style={{marginRight: '5px'}} alt="plug"/>
                  Deactivate
                </Button>
              )}
            </div>

            <DataGrid
              autoHeight
              loading={isLoading}
              rowCount={total}
              paginationMode="server"
              rows={rows}
              columns={columns}
              rowHeight={64}
              paginationModel={{
                page: page,
                pageSize: pageSize
              }}
              disableColumnMenu
              disableColumnFilter
              disableDensitySelector
              disableRowSelectionOnClick
              disableColumnSelector
              slots={{
                footer: PaginationFooter,
                columnSortedAscendingIcon: SortAsc,
                columnSortedDescendingIcon: SortDesc,
              }}
              columnVisibilityModel={{
                daily: (platformName !== 'shopify' && platformName !== 'chatgpt' && platformName !== 'claude')
              }}
              onRowClick={(params, e) => {
                e.preventDefault()
                e.stopPropagation()
                const disabled = params.row.lastBackup === '-' && (!subPlan?.isPaid || (subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED))
                if (!disabled) {
                  redirectToBackup(false, params.row.itemId, params.row.name, params.row.isRepo, params.row.projectTypeKey, params.row.status)
                }
              }}
            />
          </>
        }
      </>
    );
  }
;

export default (PlatformItemsList);
